'use client';

/**
 * useDensity — Regular vs Compact UI density (theme T4 §2).
 *
 * Default: "regular" for everyone.  Suggested default "compact" for CA /
 * Advocate / Enterprise plans (set via `setPref` after `/users/me` hydrates
 * if `user.profession === 'ca_cs' | 'advocate'` and the user hasn't picked
 * anything explicitly yet).
 *
 * Applied as `data-density="regular|compact"` on <html>; component styles
 * read it via CSS attribute selectors when needed.
 */
import { useCallback, useEffect, useState } from 'react';

import type { Density } from './types';
import { STORAGE_KEY_DENSITY } from './types';

function readDensity(): Density {
  if (typeof window === 'undefined') return 'regular';
  try {
    const v = localStorage.getItem(STORAGE_KEY_DENSITY);
    if (v === 'regular' || v === 'compact') return v;
  } catch {
    /* noop */
  }
  return 'regular';
}

function applyDensity(d: Density): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-density', d);
}

export function useDensity() {
  const [density, setDensityState] = useState<Density>('regular');

  useEffect(() => {
    const d = readDensity();
    setDensityState(d);
    applyDensity(d);
  }, []);

  const setDensity = useCallback((next: Density): void => {
    try {
      localStorage.setItem(STORAGE_KEY_DENSITY, next);
    } catch {
      /* noop */
    }
    setDensityState(next);
    applyDensity(next);
  }, []);

  return { density, setDensity };
}
